import { useState } from 'react'
import { AGENTS, HOUSES, RULES, VENDORS } from '../config/competition'
import { RUNTIME } from '../config/runtime'
import { compactMoney, duration } from '../core/format'
import type { CompetitionState } from '../core/types'
import { AgentAvatar } from './primitives/Media'

const STORE_KEY = 'utc.admin'

/**
 * Operator console. The only place the round can be opened.
 *
 * The gate here keeps the control off the public board; it is NOT access
 * control. Anyone can read a client-side key out of the bundle. When a real
 * backend owns the round, the activate call goes to it and the token is checked
 * there -- see README, "Locking down the admin route".
 */
export function AdminPage({
  state,
  onActivate,
}: {
  state: CompetitionState
  onActivate: () => void
}) {
  const [entered, setEntered] = useState(() => {
    try {
      return sessionStorage.getItem(STORE_KEY) ?? ''
    } catch {
      return ''
    }
  })
  const [draft, setDraft] = useState('')
  const [wrong, setWrong] = useState(false)

  const unlocked = entered === RUNTIME.adminKey
  const running = state.meta.phase === 'running'
  const pricesLive = state.meta.prices === 'live'

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (draft !== RUNTIME.adminKey) {
      setWrong(true)
      return
    }
    try {
      sessionStorage.setItem(STORE_KEY, draft)
    } catch {
      /* private mode: the key just will not persist across a reload */
    }
    setEntered(draft)
    setWrong(false)
  }

  return (
    <div className="admin">
      <header className="admin__bar">
        <span className="topbar__mark">UTC</span>
        <span className="admin__title">OPERATOR CONSOLE</span>
        <a className="admin__exit" href="#/">
          RETURN TO BOARD
        </a>
      </header>

      {!unlocked ? (
        <form className="admin__gate" onSubmit={submit}>
          <p className="u-label">RESTRICTED</p>
          <h1 className="admin__h1">ROUND CONTROL</h1>
          <input
            className="admin__key"
            type="password"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value)
              setWrong(false)
            }}
            placeholder="OPERATOR KEY"
            autoFocus
            aria-label="Operator key"
          />
          <button className="start__go" type="submit">
            <span>UNLOCK</span>
          </button>
          {wrong ? <p className="admin__wrong">Key rejected.</p> : null}
        </form>
      ) : (
        <div className="admin__panels">
          <section className="admin__card">
            <p className="u-label">ROUND {String(state.meta.round).padStart(2, '0')}</p>
            <h1 className="admin__h1">{running ? 'ROUND IS LIVE' : 'ROUND ON STANDBY'}</h1>
            <dl className="admin__facts">
              <div>
                <dt>SEED / AGENT</dt>
                <dd className="u-num">{compactMoney(RULES.seedCapital)}</dd>
              </div>
              <div>
                <dt>LENGTH</dt>
                <dd className="u-num">{duration(RULES.durationHours)}</dd>
              </div>
              <div>
                <dt>ENTRANTS</dt>
                <dd className="u-num">{AGENTS.length}</dd>
              </div>
              <div>
                <dt>PRICE FEED</dt>
                <dd className={`u-num ${pricesLive ? 'u-up' : 'u-down'}`}>
                  {pricesLive ? 'LIVE' : 'OFFLINE'}
                </dd>
              </div>
              <div>
                <dt>SOURCE</dt>
                <dd className="u-num">{state.meta.priceSource || '—'}</dd>
              </div>
              <div>
                <dt>TICK</dt>
                <dd className="u-num">{state.meta.tick}</dd>
              </div>
            </dl>

            <button className="start__go" onClick={onActivate} disabled={running}>
              <span>{running ? 'SESSION ACTIVE' : 'ACTIVATE TRADING SESSION'}</span>
            </button>
            <p className="admin__hint">
              {running
                ? 'The board is live. Reload to return the round to standby.'
                : 'Opens the round for every viewer. There is no undo short of a reload.'}
            </p>
          </section>

          <section className="admin__card">
            <p className="u-label">ENTRANTS</p>
            <ul className="start__roster admin__roster">
              {AGENTS.map((a) => (
                <li key={a.id} className="entrant" style={{ ['--accent' as string]: a.color }}>
                  <AgentAvatar agentId={a.id} size={26} />
                  <span className="entrant__name">{a.name}</span>
                  <span className="entrant__meta">
                    {HOUSES[a.house].name} · {VENDORS[a.vendor].short} · {a.model}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  )
}
